import type { CSSProperties } from 'react';
import type { AlertKind, AssetType } from '@ww/shared';

/** Icon path set from design/mockup-template.html (object `I`). */
export const I = {
  logo: 'M12 3a9 9 0 1 0 9 9M12 7a5 5 0 1 0 5 5M12 12l6.5-6.5',
  dash: 'M4 4h7v9H4zM13 4h7v5h-7zM13 11h7v9h-7zM4 15h7v5H4z',
  bell: 'M6 16v-5a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20.5a2 2 0 0 0 4 0',
  pin: 'M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12zM12 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  rules: 'M4 7h9M17 7h3M4 17h3M11 17h9M15 5v4M9 15v4',
  health: 'M3 12h4l2.5-6 5 12 2.5-6h4',
  bolt: 'M13 2L5 13h5l-1 9 8-11h-5z',
  /*
   * A road cone, standing on its slab foot.
   *
   * No reflective bands, and that is the whole design. `.ic` sets fill:none, so a band
   * is a horizontal stroke inside a narrow outlined triangle — which closes the apex
   * into a counter and makes the icon read as a capital **A**. Rasterised and checked
   * at 17px and 20px: with bands it is an A, without them it is a cone. The wide slab
   * is what separates it from a plain triangle or a tent; the body alone would not.
   */
  cone: 'M12 2.8 L8.4 16.3 h7.2 Z M5.4 16.3 h13.2 v3.1 h-13.2 z',
  menu: 'M4 7h16M4 12h16M4 17h16',
  gear: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7.5 19.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 15H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9.5l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 9 4.6V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1.3z',
  sound: 'M4 9.5h3.5L12 5.5v13L7.5 14.5H4zM16 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11',
  muted: 'M4 9.5h3.5L12 5.5v13L7.5 14.5H4zM16.5 9.5l5 5M21.5 9.5l-5 5',
  wall: 'M3 5h18v12H3zM8 21h8M12 17v4',
  chevD: 'M6 9l6 6 6-6',
  chevR: 'M9 6l6 6-6 6',
  chevL: 'M15 6l-6 6 6 6',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  target: 'M12 3v3M12 18v3M3 12h3M18 12h3M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  play: 'M8 5l11 7-11 7z',
  pause: 'M8 5v14M16 5v14',
  ext: 'M14 4h6v6M20 4l-9 9M18 14v6H4V6h6',
  check: 'M5 12.5l4.5 4.5L19 7',
  x: 'M6 6l12 12M18 6L6 18',
  alert: 'M12 3l10 18H2zM12 10v5M12 18v.5',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4-4',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2',
  refresh: 'M20 11a8 8 0 0 0-14.6-4.5M4 4v4h4M4 13a8 8 0 0 0 14.6 4.5M20 20v-4h-4',
  msg: 'M4 5h16v11H9l-5 4z',
  crosshair: 'M12 2v6M12 16v6M2 12h6M16 12h6M12 11.5v1',
  tornado: 'M3 5h18M5 9h14M7 13h10M9 17h6M11 21h2',
  storm: 'M7 15a4 4 0 1 1 .6-7.95A5.5 5.5 0 0 1 18 8.5a3.5 3.5 0 0 1 .5 6.5M13 12l-3 5h4l-3 5',
  watch: 'M12 3l10 18H2zM12 10v5M12 18v.5',
  flood:
    'M3 9c2.2 0 2.2-1.5 4.5-1.5S9.8 9 12 9s2.2-1.5 4.5-1.5S18.8 9 21 9M3 14c2.2 0 2.2-1.5 4.5-1.5S9.8 14 12 14s2.2-1.5 4.5-1.5S18.8 14 21 14M3 19c2.2 0 2.2-1.5 4.5-1.5S9.8 19 12 19s2.2-1.5 4.5-1.5S18.8 19 21 19',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 11v6M12 7.5v.5',
  courthouse: 'M3 21h18M5 21V11M19 21V11M9.5 21V11M14.5 21V11M2.5 10L12 4l9.5 6z',
  law: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  jail: 'M4 4h16v16H4zM9 4v16M15 4v16',
  water: 'M7 3h10v7a5 5 0 0 1-10 0zM9 14l-2 7M15 14l2 7M12 15v6',
  pumpI: 'M4 20h16M6 20v-8h12v8M9 12V7h6v5M12 7V4',
  fire: 'M12 3c.8 3.5 5 6 5 11a5 5 0 0 1-10 0c0-2.7 1.6-4 2-6.5 1.4 1 2.4 2.3 2.7 4 .4-2.5.6-5.2.3-8.5z',
  radio: 'M12 11v10M8.5 21L12 11l3.5 10M8.5 7.5a5 5 0 0 1 7 0M5.8 4.8a8.8 8.8 0 0 1 12.4 0M12 10.5v.5',
  school: 'M3 9l9-5 9 5-9 5zM7 11.5V16c3 2 7 2 10 0v-4.5',
  custom: 'M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12zM12 9v.5',
  // additions drawn in the same stroke style (not part of the mockup set)
  logout: 'M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10',
  users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 12 0v1M16 3.5a4 4 0 0 1 0 7M22 21v-1a6 6 0 0 0-4-5.6',
  fullscreen: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
  eye: 'M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6zM12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  eyeOff: 'M4 4l16 16M9.9 5.2A9.9 9.9 0 0 1 12 5c6.4 0 10 6 10 6a17 17 0 0 1-3.3 3.8M6.5 7.6C3.7 9.2 2 12 2 12s3.6 6 10 6a10 10 0 0 0 3.5-.6M10.2 10.3a2.5 2.5 0 0 0 3.4 3.5',
} as const;

export const TYPE_ICON: Record<AssetType, string> = {
  courthouse: I.courthouse,
  law: I.law,
  jail: I.jail,
  water: I.water,
  pump: I.pumpI,
  fire: I.fire,
  radio: I.radio,
  school: I.school,
  custom: I.custom,
};

export function typeIcon(type: string): string {
  return (TYPE_ICON as Record<string, string>)[type] ?? I.custom;
}

export const KIND_ICON: Record<AlertKind, string> = {
  tor: I.tornado,
  // No dedicated ice or snowflake glyph in the set; the alert triangle is honest about
  // severity and the existing storm glyph reads as winter weather well enough.
  ice: I.alert,
  snow: I.storm,
  svr: I.storm,
  watch: I.watch,
  flood: I.flood,
  sps: I.info,
};

export function Icon({
  d,
  size = 20,
  style,
  className,
  strokeWidth,
}: {
  d: string;
  size?: number;
  style?: CSSProperties;
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={className ? `ic ${className}` : 'ic'}
      style={{ width: size, height: size, ...(strokeWidth ? { strokeWidth } : {}), ...style }}
    >
      <path d={d} />
    </svg>
  );
}
